import { apiCall } from '../../services/apiUtil';
import { transformEventFromAPI } from '../../utils/dateTimeUtils';

/**
 * Events API Service
 *
 * Centralized API calls for all event-related operations
 */

/**
 * Fetch events for a specific owner (group/squad/tournament)
 * @param {string} ownerType - Type of owner (e.g., 'group', 'squad', 'tournament')
 * @param {string} ownerId - ID of the owner
 * @param {string} startDate - Optional start date filter
 * @param {string} endDate - Optional end date filter
 * @param {boolean} includePast - Whether to include past events
 * @returns {Promise<Array>} Array of events
 */
export const fetchEvents = async (ownerType, ownerId, startDate = null, endDate = null, includePast = false) => {
  if (!ownerType || !ownerId) {
    throw new Error('ownerType and ownerId are required');
  }

  const params = new URLSearchParams({
    ownerType,
    ownerId: ownerId
  });

  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (includePast) params.append('includePast', 'true');

  const eventsList = await apiCall(`/events?${params.toString()}`);

  // Transform events - ensure timestamps are in correct format
  return eventsList.map(event => {
    const responses = event.responses.map(r => ({
      userId: r.userId,
      username: r.user?.first_name && r.user?.last_name
        ? `${r.user.first_name} ${r.user.last_name}`
        : `User ${r.userId}`,
      status: r.status,
      arrivalTime: r.arrivalTime || null
    }));

    // Use centralized transformer for timestamp handling
    const transformed = transformEventFromAPI(event);

    return {
      id: transformed.id,
      ownerType: transformed.ownerType,
      ownerId: transformed.ownerId,
      subject: transformed.subject,
      venue: transformed.venue || null,
      start_time: transformed.start_time,
      end_time: transformed.end_time,
      status: transformed.status || 'open',
      createdAt: transformed.createdAt,
      createdBy: transformed.createdBy,
      responses: responses,
      permitUrl: transformed.permitUrl || null,
      // Session fields. `capacity` keeps null distinct from 0: null is
      // unlimited, 0 is a session with no spots left to give.
      capacity: transformed.capacity ?? null,
      courtIds: transformed.courtIds ?? [],
      eventKind: transformed.eventKind ?? null,
      // 'invitees' when the event is for the people invited to it; null for an
      // older event, which is for the whole squad.
      audience: event.audience ?? null,
      // Set when a court's hold came from a session, which owns it.
      parentEventId: transformed.parentEventId ?? null,
      // What a pattern change did, when this response is one. Carried because
      // every transform between here and the screen builds an explicit object,
      // so anything unnamed is silently dropped.
      reshaped: transformed.reshaped ?? null,
      // Already resolved server-side through event → series → programme.
      prices:     Array.isArray(transformed.prices) ? transformed.prices : [],
      priceLevel: transformed.priceLevel ?? null,
      recurrenceRuleId: transformed.recurrenceRuleId ?? null,
      recurrenceIndex: transformed.recurrenceIndex ?? null,
      isRecurrenceException: transformed.isRecurrenceException ?? false,
      recurrenceRule: transformed.recurrenceRule ?? null,
    };
  });
};

/**
 * Create a new event
 * @param {string} ownerType - Type of owner
 * @param {string} ownerId - ID of the owner
 * @param {object} eventData - Event data (subject, address, date/startTime/endTime OR start_time/end_time)
 * @returns {Promise<object>} Created event
 */
export const createEvent = async (ownerType, ownerId, eventData) => {
  // Handle both timestamp format and legacy date+time format
  let start_time, end_time;

  if (eventData.start_time && eventData.end_time) {
    // New format: timestamps provided directly
    start_time = eventData.start_time;
    end_time = eventData.end_time;
  } else if (eventData.date && eventData.startTime && eventData.endTime) {
    // Convert date + time (in user's local timezone) to UTC timestamps
    const startDateTime = new Date(`${eventData.date}T${eventData.startTime}:00`);
    const endDateTime = new Date(`${eventData.date}T${eventData.endTime}:00`);

    // Handle midnight-crossing events
    if (eventData.endTime < eventData.startTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    start_time = startDateTime.toISOString();
    end_time = endDateTime.toISOString();
  } else {
    throw new Error('Invalid event data: must provide either start_time/end_time or date/startTime/endTime');
  }

  const payload = {
    owner_type: ownerType,
    owner_id: ownerId,
    subject: eventData.subject,
    start_time,
    end_time,
    permit_url:     eventData.permitUrl || null,
    // Optional session details. Only programme sessions use them; the server
    // refuses courts on any other owner type.
    capacity:       eventData.capacity ?? null,
    court_ids:      eventData.courtIds || [],
    venue_name:     eventData.locationName || null,
    venue_address:  eventData.address || null,
    venue_place_id: eventData.placeId || null,
    venue_lat:      eventData.lat || null,
    venue_lng:      eventData.lng || null,
    venue_city:     eventData.city || null,
    venue_state:    eventData.state || null,
    venue_country:  eventData.country || null,
    venue_zip_code: eventData.zipCode || null
  };

  if (eventData.recurrence) {
    payload.recurrence = eventData.recurrence;
  }

  // Who a squad event invites. Left out, the squad decides.
  if (Array.isArray(eventData.invitees)) {
    payload.invitees = eventData.invitees;
  }

  const result = await apiCall('/events', {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  // Use centralized transformer for timestamp handling
  const transformed = transformEventFromAPI(result);

  return {
    id: transformed.id,
    ownerType: transformed.ownerType,
    ownerId: transformed.ownerId,
    subject: transformed.subject,
    venue: transformed.venue || null,
    start_time: transformed.start_time,
    end_time: transformed.end_time,
    status: transformed.status || 'open',
    createdAt: transformed.createdAt,
    createdBy: transformed.createdBy,
    responses: [],
    permitUrl: transformed.permitUrl || null,
    /* THE SESSION FIELDS, which this used to drop on the floor.
     *
     * The caller appends what comes back straight into the list it is already
     * showing — that is what puts the new row on screen without a refetch — so
     * anything missing here is missing from the screen. The server sends all of
     * these on the 201; leaving them unnamed meant a host who added a night for
     * twelve saw a session with no spots, no courts and no price, and it stayed
     * that way until they reloaded the page and the list was fetched properly.
     *
     * The same explicit-object trap the response transform above warns about,
     * sprung in the one place where nothing goes back to the server to correct
     * it. Kept in step with that transform: a field added there belongs here.
     */
    capacity:   transformed.capacity ?? null,
    courtIds:   transformed.courtIds ?? [],
    eventKind:  transformed.eventKind ?? null,
    parentEventId: transformed.parentEventId ?? null,
    prices:     Array.isArray(transformed.prices) ? transformed.prices : [],
    priceLevel: transformed.priceLevel ?? null,
    // Set when the POST created a series rather than one event. The caller
    // needs it to know the single row it got back is not the whole result.
    recurrenceRuleId: transformed.recurrenceRuleId ?? null,
    recurrenceIndex: transformed.recurrenceIndex ?? null,
    isRecurrenceException: transformed.isRecurrenceException ?? false,
    recurrenceRule: transformed.recurrenceRule ?? null
  };
};

/**
 * Update an existing event
 * @param {number} eventId - Event ID
 * @param {object} eventData - Updated event data
 * @param {string} scope - Recurrence scope: 'this' | 'following' | 'all'
 * @returns {Promise<object>} Updated event
 */
export const updateEvent = async (eventId, eventData, scope = 'this') => {
  const payload = {};
  if (eventData.subject !== undefined) payload.subject = eventData.subject;
  // Venue fields
  if (eventData.locationName !== undefined) payload.venue_name    = eventData.locationName || null;
  if (eventData.address     !== undefined) payload.venue_address  = eventData.address || null;
  if (eventData.placeId     !== undefined) payload.venue_place_id = eventData.placeId || null;
  if (eventData.lat         !== undefined) payload.venue_lat      = eventData.lat || null;
  if (eventData.lng         !== undefined) payload.venue_lng      = eventData.lng || null;
  if (eventData.city        !== undefined) payload.venue_city     = eventData.city || null;
  if (eventData.state       !== undefined) payload.venue_state    = eventData.state || null;
  if (eventData.country     !== undefined) payload.venue_country  = eventData.country || null;
  if (eventData.zipCode     !== undefined) payload.venue_zip_code = eventData.zipCode || null;

  // Session fields. Sent only when the caller supplied them: the server reads
  // an absent field as "leave alone", so defaulting them here would turn every
  // squad-event edit into a silent clear of capacity and court holds.
  if (eventData.capacity !== undefined) payload.capacity  = eventData.capacity;
  if (eventData.courtIds !== undefined) payload.court_ids = eventData.courtIds;

  // A new pattern for the series. Same rule as above — sent only when given,
  // because the server reshapes the occurrences to match whatever arrives here
  // and an accidental default would rebuild somebody's whole season.
  if (eventData.recurrence !== undefined) payload.recurrence = eventData.recurrence;

  // Handle both timestamp format and legacy date+time format
  if (eventData.start_time !== undefined && eventData.end_time !== undefined) {
    // New format: timestamps provided directly
    payload.start_time = eventData.start_time;
    payload.end_time = eventData.end_time;
  } else if (eventData.date !== undefined && eventData.startTime !== undefined && eventData.endTime !== undefined) {
    // Legacy format: convert date + time (in user's local timezone) to UTC timestamps
    const startDateTime = new Date(`${eventData.date}T${eventData.startTime}:00`);
    const endDateTime = new Date(`${eventData.date}T${eventData.endTime}:00`);

    // Handle midnight-crossing events
    if (eventData.endTime < eventData.startTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    payload.start_time = startDateTime.toISOString();
    payload.end_time = endDateTime.toISOString();
  }

  const result = await apiCall(`/events/${eventId}?scope=${scope}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });

  // Use centralized transformer for timestamp handling
  return transformEventFromAPI(result);
};

/**
 * Delete an event
 * @param {number} eventId - Event ID
 * @param {string} scope - Recurrence scope: 'this' | 'following' | 'all'
 * @returns {Promise<void>}
 */
export const deleteEvent = async (eventId, scope = 'this') => {
  await apiCall(`/events/${eventId}?scope=${scope}`, { method: 'DELETE' });
};

/**
 * Update event response (RSVP)
 * @param {number} eventId - Event ID
 * @param {string} status - Response status ('going', 'maybe', 'not_going')
 * @param {number} userId - Optional user ID (defaults to current user)
 * @returns {Promise<object>} Updated response
 */
export const fetchUpcomingEvents = async () => {
  return await apiCall('/events/upcoming');
};

export const updateEventResponse = async (eventId, status, userId = null, arrivalTime = null, applyToSeries = false) => {
  const payload = { status };
  if (userId) payload.userId = userId;
  if (arrivalTime) payload.arrivalTime = arrivalTime;
  if (applyToSeries) payload.applyToSeries = true;
  return await apiCall(`/events/${eventId}/responses`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

/**
 * Get all responses for an event
 * @param {number} eventId - Event ID
 * @returns {Promise<Array>} Array of responses
 */
export const getEventResponses = async (eventId) => {
  const responses = await apiCall(`/events/${eventId}/responses`);
  return responses.map(r => ({
    userId: r.userId,
    username: r.user?.first_name && r.user?.last_name
      ? `${r.user.first_name} ${r.user.last_name}`
      : `User ${r.userId}`,
    status: r.status,
    arrivalTime: r.arrivalTime || null
  }));
};

/**
 * Update event status
 * @param {number} eventId - Event ID
 * @param {string} status - New status ('open', 'cancelled', 'completed')
 * @returns {Promise<object>} Updated event
 */
export const updateEventStatus = async (eventId, status) => {
  return await apiCall(`/events/${eventId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  });
};
